import type { CourierSettlementChargeLine } from '../../models/courierSettlementChargeLine';
import { getChargeLineBillingStatus, type ChargeLineBillingStatus } from '../../models/courierSettlementChargeLine';
import { formatKgEs } from '../lib/chargeLineDisplay';
import { StatusBadgeWithDot } from '@modules/Orders/List/components/display/StatusBadgeWithDot';
import {
  defaultLogisticStatusDot,
  defaultOrderStatusDot,
  logisticStatusColorMap,
  statusColorMap,
} from '@modules/Orders/List/config/orderStatusConfig';
import { Shop } from '@shared/components/ui/shop';
import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/** Punto semántico para `match_status` cuando la API no envía código numérico. */
function matchStatusDotClass(status: string | null | undefined): string {
  const key = (status || '').toLowerCase();
  if (key.includes('unmatched') || key.includes('error') || key.includes('fail')) return 'bg-bright-error';
  if (key.includes('matched')) return 'bg-green-500';
  if (key.includes('pending') || key.includes('partial')) return 'bg-bright-warning';
  return defaultOrderStatusDot.dot;
}

function billingDotClass(status: ChargeLineBillingStatus): string {
  switch (status) {
    case 'invoiced':
      return 'bg-green-500';
    case 'pending_invoice':
      return 'bg-bright-warning';
    default:
      return 'bg-muted-foreground';
  }
}

export function useSettlementChargeLinesAgGridColumns() {
  const { t } = useTranslation('courierInvoicesModule');

  const columnDefs: ColDef<CourierSettlementChargeLine>[] = useMemo(() => {
    const matchLabel = (status: string | null | undefined) => {
      if (!status) return '-';
      const key = `settlement.charge_lines.match_status.${status}`;
      return t(key, { defaultValue: status.replace(/_/g, ' ') });
    };

    return [
      {
        field: 'id',
        headerName: t('settlement.charge_lines.columns.line_id'),
        minWidth: 96,
        maxWidth: 120,
        pinned: 'left',
        lockPosition: true,
        sortable: false,
        valueFormatter: (p) => (p.value != null ? String(p.value) : '-'),
        cellClass: 'text-muted-foreground tabular-nums text-xs',
      },
      {
        field: 'source_order_id',
        headerName: t('settlement.charge_lines.columns.source_order_id'),
        minWidth: 120,
        sortable: false,
        valueFormatter: (p) => (p.value != null ? String(p.value) : '-'),
        cellClass: 'tabular-nums text-sm font-medium',
      },
      {
        field: 'match_status',
        headerName: t('settlement.charge_lines.columns.match'),
        minWidth: 140,
        sortable: false,
        cellRenderer: (params: { data?: CourierSettlementChargeLine }) => {
          const raw = params.data?.match_status;
          return (
            <StatusBadgeWithDot className="text-xs" dotClassName={matchStatusDotClass(raw)} label={matchLabel(raw)} />
          );
        },
      },
      {
        colId: 'order_ref',
        headerName: t('settlement.charge_lines.columns.ref'),
        minWidth: 120,
        flex: 0.85,
        sortable: false,
        cellRenderer: (params: { data?: CourierSettlementChargeLine }) => (
          <span className="text-sm font-medium">{params.data?.order?.ref ?? '-'}</span>
        ),
      },
      {
        colId: 'shop_name',
        headerName: t('settlement.charge_lines.columns.shop'),
        minWidth: 140,
        flex: 1,
        sortable: false,
        cellRenderer: (params: { data?: CourierSettlementChargeLine }) => {
          const shop = params.data?.order?.shop;
          if (!shop?.name) {
            return <span className="text-sm text-complementary-text">-</span>;
          }
          return <Shop name={shop.name} src={shop.image_hash ?? undefined} className="py-0" />;
        },
      },
      {
        colId: 'team_name',
        headerName: t('settlement.charge_lines.columns.client'),
        minWidth: 140,
        flex: 1,
        sortable: false,
        cellRenderer: (params: { data?: CourierSettlementChargeLine }) => {
          const name = params.data?.order?.team?.name;
          if (!name) {
            return <span className="text-sm text-complementary-text">-</span>;
          }
          return <span className="text-sm font-medium">{name}</span>;
        },
      },
      {
        colId: 'order_status',
        headerName: t('settlement.charge_lines.columns.order_status'),
        minWidth: 130,
        flex: 0.95,
        sortable: false,
        cellRenderer: (params: { data?: CourierSettlementChargeLine }) => {
          const st = params.data?.order?.status;
          const label = st?.description ?? st?.name;
          if (!label) {
            return <StatusBadgeWithDot className="text-xs" dotClassName={defaultOrderStatusDot.dot} label="-" />;
          }
          const code = st?.id;
          const dot =
            typeof code === 'number' && code > 0
              ? (statusColorMap[code]?.dot ?? defaultOrderStatusDot.dot)
              : defaultOrderStatusDot.dot;
          return <StatusBadgeWithDot className="text-xs" dotClassName={dot} label={label} />;
        },
      },
      {
        colId: 'logistics_status',
        headerName: t('settlement.charge_lines.columns.logistics_status'),
        minWidth: 150,
        flex: 0.95,
        sortable: false,
        cellRenderer: (params: { data?: CourierSettlementChargeLine }) => {
          const st = params.data?.order?.shipment_status;
          const label = st?.name;
          if (!label) {
            return (
              <StatusBadgeWithDot
                className="text-xs"
                dotClassName={defaultLogisticStatusDot.dot}
                label={t('settlement.charge_lines.no_logistic_status')}
              />
            );
          }
          const code = st?.id;
          const dot =
            typeof code === 'number' && code > 0
              ? (logisticStatusColorMap[code]?.dot ?? defaultLogisticStatusDot.dot)
              : defaultLogisticStatusDot.dot;
          return <StatusBadgeWithDot className="text-xs" dotClassName={dot} label={label} />;
        },
      },
      {
        field: 'charged_weight_kg',
        headerName: t('settlement.charge_lines.columns.charged_weight_kg'),
        minWidth: 150,
        sortable: false,
        cellRenderer: (params: { data?: CourierSettlementChargeLine }) => (
          <span className="text-sm tabular-nums">{formatKgEs(params.data?.charged_weight_kg)}</span>
        ),
      },
      {
        field: 'our_weight_kg',
        headerName: t('settlement.charge_lines.columns.our_weight_kg'),
        minWidth: 140,
        sortable: false,
        cellRenderer: (params: { data?: CourierSettlementChargeLine }) => (
          <span className="text-sm tabular-nums">{formatKgEs(params.data?.our_weight_kg)}</span>
        ),
      },
      {
        field: 'our_shipment_type',
        headerName: t('settlement.charge_lines.columns.our_shipment_type'),
        minWidth: 160,
        flex: 0.9,
        sortable: false,
        valueFormatter: (p) => (p.value != null && String(p.value).trim() !== '' ? String(p.value) : '-'),
        cellClass: 'text-sm',
      },
      {
        colId: 'billing',
        headerName: t('settlement.charge_lines.columns.billing'),
        minWidth: 140,
        sortable: false,
        cellRenderer: (params: { data?: CourierSettlementChargeLine }) => {
          const line = params.data;
          if (!line) return null;
          const billing = getChargeLineBillingStatus(line);
          const label =
            billing === 'invoiced'
              ? t('settlement.charge_lines.billing.invoiced')
              : billing === 'pending_invoice'
                ? t('settlement.charge_lines.billing.pending_invoice')
                : t('settlement.charge_lines.billing.not_invoiced');
          return <StatusBadgeWithDot className="text-xs" dotClassName={billingDotClass(billing)} label={label} />;
        },
      },
    ];
  }, [t]);

  return { columnDefs };
}
